declare var Autodesk: any;

export interface DisplayProp {
  label: string;
  value: string;
}

export interface LayerRow {
  label: string;
  value: string;
}

export interface LayerGroup {
  name: string;
  rows: LayerRow[];
}

export interface PresentationNestedElement {
  Name?: string;
  [key: string]: string | undefined;
}

export interface PresentationJson {
  Name?: string;
  NestedElements: PresentationNestedElement[];
}

const PRESENTATION_PARAM_NAME: string = 'Bg Viewer Presentation';

const ALLOWED_NAMES: ReadonlySet<string> = new Set([
  'Name',
  'Area',
  'Object Area',
  'Area With Opening',
  'Volume',
  'Length',
  'Object Length',
  'Thickness',
  'Foundation Thickness',
  'BG Stringer Length',
  'Bar Length',
  'Level',
  'Base Level',
  'Width',
  'Height',
  'Foundation Height',
]);

const DISPLAY_ORDER: string[] = [
  'Name',
  'Area',
  'Object Area',
  'Area With Opening',
  'Volume',
  'Length',
  'BG Stringer Length',
  'Bar Length',
  'Object Length',
  'Thickness',
  'Foundation Thickness',
  'Foundation Height',
  'Level',
  'Base Level',
  'Width',
  'Height',
];

function normalizeUnit(unit: unknown): string {
  if (typeof unit !== 'string') return '';

  const trimmed = unit.trim();
  if (!trimmed) return '';

  const unitToken = trimmed
    .split(':')
    .pop()
    ?.replace(/-\d+(?:\.\d+)*$/, '')
    .trim();

  if (!unitToken) return '';

  const unitMap: Record<string, string> = {
    feet: 'ft',
    inches: 'in',
    millimeters: 'mm',
    centimeters: 'cm',
    meters: 'm',
    squareFeet: 'ft²',
    cubicFeet: 'ft³',
    squareMeters: 'm²',
    cubicMeters: 'm³',
    feetFractionalInches: 'ft-in',
    degrees: 'deg',
    radians: 'rad',
    unitless: '',
  };

  if (Object.prototype.hasOwnProperty.call(unitMap, unitToken)) {
    return unitMap[unitToken];
  }

  // Fallback: convert camelCase token to a readable lowercase phrase.
  return unitToken
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .toLowerCase();
}

function appendUnit(value: string, unit: string): string {
  if (!unit) return value;
  if (!value) return value;
  if (value.toLowerCase().endsWith(unit.toLowerCase())) return value;
  return `${value} ${unit}`;
}

function formatDisplayValue(raw: unknown, unit: unknown): string {
  const normalizedUnit = normalizeUnit(unit);

  if (typeof raw === 'number' && Number.isFinite(raw)) {
    const numericValue = raw.toFixed(2).replace(/\.?0+$/, '');
    return appendUnit(numericValue, normalizedUnit);
  }

  const textValue = String(raw).trim();
  return appendUnit(textValue, normalizedUnit);
}

function parseLayers(
  bgParamMap: Map<string, string>,
  fallbackManufacturer: string
): LayerGroup[] {
  // Determine max layer count across all Builtgenix params that contain ':'
  let maxCount = 1;
  for (const value of bgParamMap.values()) {
    const segments = value.split(':');
    if (segments.length > maxCount) maxCount = segments.length;
  }

  const groups: LayerGroup[] = [];

  for (let i = 0; i < maxCount; i++) {
    // Derive layer name from 'Builtgenix Item' segment
    const itemKey = Array.from(bgParamMap.keys()).find((k) =>
      /^builtgenix\s+item$/i.test(k)
    );
    const itemSegments = itemKey ? bgParamMap.get(itemKey)!.split(':') : [];
    const itemValue = (itemSegments[i] ?? '').trim();
    const layerName = itemValue || 'Item';

    const rows: LayerRow[] = [];

    // Emit a row for every Builtgenix param
    for (const [paramName, paramValue] of bgParamMap.entries()) {
      const segments = paramValue.split(':');
      const segVal = (segments[i] ?? '').trim();

      const shortLabel = paramName
        .replace(/builtgenix/i, '')
        .replace(/^[\s_\-:]+|[\s_\-:]+$/g, '')
        .trim();

      rows.push({ label: shortLabel || paramName, value: segVal });
    }

    // Add Manufacturer if 'Builtgenix Manufacturer' is not among params
    const hasBuiltgenixManufacturer = Array.from(bgParamMap.keys()).some((k) =>
      /^builtgenix\s+manufacturer$/i.test(k)
    );
    if (!hasBuiltgenixManufacturer && fallbackManufacturer) {
      rows.push({ label: 'Manufacturer', value: fallbackManufacturer });
    }

    groups.push({ name: `Layer ${i + 1} – ${layerName}`, rows });
  }

  return groups;
}

function extractPresentationJson(rawProps: any[]): PresentationJson | null {
  const prop = rawProps.find(
    (p) => (p.displayName ?? '').trim() === PRESENTATION_PARAM_NAME
  );
  if (!prop) return null;

  const raw = prop.displayValue;
  if (raw === null || raw === undefined) return null;
  const str = String(raw).trim();
  if (!str) return null;

  try {
    const parsed = JSON.parse(str);
    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      !Array.isArray(parsed.NestedElements) ||
      parsed.NestedElements.length === 0
    ) {
      return null;
    }
    return parsed as PresentationJson;
  } catch {
    return null;
  }
}

function nestedElementToProps(el: PresentationNestedElement): DisplayProp[] {
  return Object.entries(el)
    .filter(([key, val]) => {
      if (key === 'Name') return false;
      if (val === null || val === undefined) return false;
      return String(val).trim() !== '';
    })
    .map(([key, val]) => ({ label: key, value: String(val!).trim() }));
}

export function filterViewerProperties(rawProps: any[]): {
  bgProps: DisplayProp[];
  otherProps: DisplayProp[];
  layerGroups: LayerGroup[];
  presentationJson: PresentationJson | null;
} {
  const presentationJson = extractPresentationJson(rawProps);
  const bgProps: DisplayProp[] = [];
  const otherMap = new Map<string, DisplayProp>();
  // Map of original param name (trimmed) → formatted value
  const bgParamMap = new Map<string, string>();
  let fallbackManufacturer = '';

  for (const prop of rawProps) {
    const name: string = (prop.displayName ?? '').trim();
    const raw = prop.displayValue;
    const unit = prop.units ?? prop.displayUnits ?? prop.unit;

    if (raw === null || raw === undefined) continue;
    const value = formatDisplayValue(raw, unit);
    if (value === '' || value === 'undefined' || value === 'null') continue;

    if (/builtgenix/i.test(name)) {
      const label = name
        .replace(/builtgenix/i, '')
        .replace(/^[\s_\-:]+|[\s_\-:]+$/g, '')
        .trim();
      bgProps.push({ label: label || name, value });
      bgParamMap.set(name, value);
    } else {
      if (ALLOWED_NAMES.has(name) && !otherMap.has(name)) {
        otherMap.set(name, { label: name, value });
      }
      if (/^manufacturer$/i.test(name) && !fallbackManufacturer) {
        fallbackManufacturer = value;
      }
    }
  }

  const otherProps = Array.from(otherMap.values()).sort((a, b) => {
    const ai = DISPLAY_ORDER.indexOf(a.label);
    const bi = DISPLAY_ORDER.indexOf(b.label);
    return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
  });

  // Only build layer groups when at least one Builtgenix value contains ':'
  const hasLayers = Array.from(bgParamMap.values()).some((v) => v.includes(':'));
  const layerGroups = hasLayers
    ? parseLayers(bgParamMap, fallbackManufacturer)
    : [];

  return { bgProps, otherProps, layerGroups, presentationJson };
}

let panelStylesInjected = false;

function injectPanelStyles(): void {
  if (panelStylesInjected) return;
  panelStylesInjected = true;

  const style = document.createElement('style');
  // Keep styling close to Forge viewer's default property panel look
  style.textContent = `
    #bg-properties-panel {
      font-family: Arial, Helvetica, sans-serif;
      font-size: 13px;
      min-width: 240px;
    }
    #bg-properties-panel .bg-panel-content {
      display: flex;
      flex-direction: column;
      height: calc(100% - 36px);
      background: #2f3135;
    }
    .bg-panel-search-wrap {
      padding: 6px 8px;
      border-bottom: 1px solid #3a3d41;
      flex-shrink: 0;
    }
    .bg-panel-search {
      width: 100%;
      padding: 5px 8px;
      box-sizing: border-box;
      border: 1px solid #4a4d52;
      border-radius: 0;
      font-size: 12px;
      outline: none;
      background: #26282b;
      color: #d7d7d7;
      transition: border-color 0.15s;
    }
    .bg-panel-search::placeholder {
      color: #8a8d92;
    }
    .bg-panel-search:focus {
      border-color: #6c7179;
    }
    .bg-panel-scroll {
      flex: 1;
      overflow-y: auto;
      overflow-x: hidden;
      padding-bottom: 6px;
    }
    .bg-panel-scroll::-webkit-scrollbar {
      width: 8px;
    }
    .bg-panel-scroll::-webkit-scrollbar-track {
      background: #2f3135;
    }
    .bg-panel-scroll::-webkit-scrollbar-thumb {
      background: #52565e;
      border-radius: 0;
    }
    .bg-panel-scroll::-webkit-scrollbar-thumb:hover {
      background: #6c7179;
    }
    .bg-panel-empty {
      text-align: center;
      color: #a2a5ab;
      margin-top: 40px;
      font-size: 12px;
      padding: 0 14px;
      line-height: 1.6;
    }
    .bg-section {
      margin: 0;
      border-bottom: 1px solid #3a3d41;
    }
    .bg-section-header {
      font-size: 13px;
      font-weight: 600;
      color: #ebebeb;
      background: #3a3d41;
      padding: 7px 10px;
      cursor: pointer;
      user-select: none;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .bg-section-header::before {
      content: '\\25BE';
      display: inline-block;
      width: 14px;
      color: #cfd2d6;
      margin-right: 2px;
      font-size: 11px;
      transform: translateY(-1px);
    }
    .bg-section.bg-section--collapsed .bg-section-header::before {
      content: '\\25B8';
      transform: translateY(-1px);
    }
    .bg-section.bg-section--collapsed .bg-prop-table {
      display: none;
    }
    .bg-prop-table {
      width: 100%;
      border-collapse: collapse;
      table-layout: fixed;
    }
    .bg-prop-row {
      transition: background 0.1s linear;
      cursor: default;
    }
    .bg-prop-row:hover {
      background: #3f4247;
    }
    .bg-prop-label {
      padding: 5px 8px 5px 10px;
      color: #b8bcc2;
      vertical-align: top;
      width: 48%;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      font-size: 11px;
      border-top: 1px solid #393c41;
    }
    .bg-prop-value {
      padding: 5px 10px 5px 8px;
      color: #e0e3e7;
      font-weight: 400;
      vertical-align: top;
      width: 52%;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      font-size: 11px;
      border-top: 1px solid #393c41;
    }
    .bg-layer-group {
      border-bottom: 1px solid #3a3d41;
    }
    .bg-layer-group-header {
      font-size: 11px;
      font-weight: 600;
      color: #c8c8c8;
      background: #35383d;
      padding: 5px 10px 5px 20px;
      cursor: pointer;
      user-select: none;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .bg-layer-group-header::before {
      content: '\\25BE';
      display: inline-block;
      width: 12px;
      color: #9aa0aa;
      margin-right: 2px;
      font-size: 10px;
    }
    .bg-layer-group.bg-layer-group--collapsed .bg-layer-group-header::before {
      content: '\\25B8';
    }
    .bg-layer-group.bg-layer-group--collapsed .bg-prop-table {
      display: none;
    }
  `;
  document.head.appendChild(style);
}

export function createBgPropertiesPanel(viewer: any): any {
  injectPanelStyles();

  function BgPropertiesPanel(this: any, parentContainer: HTMLElement) {
    Autodesk.Viewing.UI.DockingPanel.call(
      this,
      parentContainer,
      'bg-properties-panel',
      'Element Properties'
    );

    this.container.style.width = '300px';
    this.container.style.height = '500px';
    this.container.style.top = '80px';
    this.container.style.right = '10px';
    this.container.style.left = 'unset';
    this.container.style.resize = 'both';
    this.container.style.minWidth = '240px';
    this.container.style.minHeight = '180px';

    this._allRows = [];
    this._bgSection = null;
    this._otherSection = null;
    this._layersSection = null;
    this._presentationSections = [];
    this._searchInput = null;
    this._scrollArea = null;
    this._emptyMsg = null;

    this._buildUI();
  }

  BgPropertiesPanel.prototype = Object.create(
    Autodesk.Viewing.UI.DockingPanel.prototype
  );
  BgPropertiesPanel.prototype.constructor = BgPropertiesPanel;

  BgPropertiesPanel.prototype._buildUI = function (this: any): void {
    const content = document.createElement('div');
    content.className = 'bg-panel-content';

    const searchWrap = document.createElement('div');
    searchWrap.className = 'bg-panel-search-wrap';
    const searchInput = document.createElement('input');
    searchInput.type = 'text';
    searchInput.className = 'bg-panel-search';
    searchInput.placeholder = 'Search parameters\u2026';
    searchInput.addEventListener('input', () =>
      this._applySearch(searchInput.value)
    );
    this._searchInput = searchInput;
    searchWrap.appendChild(searchInput);

    const scroll = document.createElement('div');
    scroll.className = 'bg-panel-scroll';
    this._scrollArea = scroll;

    const empty = document.createElement('div');
    empty.className = 'bg-panel-empty';
    empty.innerHTML = 'Select an element<br>to view its properties.';
    this._emptyMsg = empty;
    scroll.appendChild(empty);

    content.appendChild(searchWrap);
    content.appendChild(scroll);
    this.container.appendChild(content);
  };

  BgPropertiesPanel.prototype.updateTitle = function (
    this: any,
    elementName?: string
  ): void {
    // this.title is the title bar DOM element created by the parent DockingPanel
    if (this.title) {
      this.title.textContent = elementName
        ? elementName
        : 'Element Properties';
    }
  };

  BgPropertiesPanel.prototype.updateProperties = function (
    this: any,
    bgProps: DisplayProp[],
    otherProps: DisplayProp[],
    elementName?: string,
    layerGroups?: LayerGroup[],
    presentationJson?: PresentationJson | null
  ): void {
    this._allRows = [];
    this._bgSection = null;
    this._otherSection = null;
    this._layersSection = null;
    this._presentationSections = [];
    this._scrollArea.innerHTML = '';

    // New path: use presentation JSON when present and valid
    if (presentationJson) {
      const title = (presentationJson.Name ?? '').trim() || 'Element Properties';
      this.updateTitle(title);

      // Details section (ALLOWED_NAMES properties) renders first
      if (otherProps.length > 0) {
        const detailsSection = this._buildSection('Details', otherProps, false);
        this._presentationSections.push(detailsSection);
        this._scrollArea.appendChild(detailsSection);
      }

      this._buildPresentationSections(presentationJson);
      if (this._searchInput?.value) {
        this._applySearch(this._searchInput.value);
      }
      return;
    }

    // Old path
    this.updateTitle(elementName);

    const hasLayers = layerGroups && layerGroups.length > 0;

    if (bgProps.length === 0 && otherProps.length === 0 && !hasLayers) {
      const noPropMsg = document.createElement('div');
      noPropMsg.className = 'bg-panel-empty';
      noPropMsg.textContent = 'No matching properties found for this element.';
      this._scrollArea.appendChild(noPropMsg);
      return;
    }

    if (bgProps.length > 0) {
      this._bgSection = this._buildSection('Builtgenix Parameters', bgProps, true);
      this._scrollArea.appendChild(this._bgSection);
    }

    if (otherProps.length > 0) {
      this._otherSection = this._buildSection('Properties', otherProps, false);
      this._scrollArea.appendChild(this._otherSection);
    }

    if (this._searchInput?.value) {
      this._applySearch(this._searchInput.value);
    }
  };

  BgPropertiesPanel.prototype.clearProperties = function (this: any): void {
    this.updateTitle();
    this._allRows = [];
    this._bgSection = null;
    this._otherSection = null;
    this._layersSection = null;
    this._presentationSections = [];
    this._scrollArea.innerHTML = '';
    this._scrollArea.appendChild(this._emptyMsg);
    if (this._searchInput) {
      this._searchInput.value = '';
    }
  };

  BgPropertiesPanel.prototype._buildSection = function (
    this: any,
    title: string,
    props: DisplayProp[],
    isBuiltgenix: boolean
  ): HTMLElement {
    const section = document.createElement('div');
    section.className = isBuiltgenix
      ? 'bg-section bg-section--builtgenix'
      : 'bg-section';

    const header = document.createElement('div');
    header.className = 'bg-section-header';
    header.textContent = title;
    header.addEventListener('click', () => {
      section.classList.toggle('bg-section--collapsed');
    });
    section.appendChild(header);

    const table = document.createElement('table');
    table.className = 'bg-prop-table';

    props.forEach((prop) => {
      const row = document.createElement('tr');
      row.className = 'bg-prop-row';
      (row as HTMLElement & { dataset: DOMStringMap }).dataset['label'] =
        prop.label.toLowerCase();
      (row as HTMLElement & { dataset: DOMStringMap }).dataset['value'] =
        prop.value.toLowerCase();

      const tdLabel = document.createElement('td');
      tdLabel.className = 'bg-prop-label';
      tdLabel.textContent = prop.label;

      const tdValue = document.createElement('td');
      tdValue.className = 'bg-prop-value';
      tdValue.textContent = prop.value;

      row.appendChild(tdLabel);
      row.appendChild(tdValue);
      table.appendChild(row);
      this._allRows.push(row);
    });

    section.appendChild(table);
    return section;
  };

  BgPropertiesPanel.prototype._buildPresentationSections = function (
    this: any,
    presentationJson: PresentationJson
  ): void {
    const nested = presentationJson.NestedElements;

    if (nested.length === 1) {
      const props = nestedElementToProps(nested[0]);
      if (props.length > 0) {
        const section = this._buildSection('Parameters', props, false);
        this._presentationSections.push(section);
        this._scrollArea.appendChild(section);
      } else {
        const noPropMsg = document.createElement('div');
        noPropMsg.className = 'bg-panel-empty';
        noPropMsg.textContent = 'No matching properties found for this element.';
        this._scrollArea.appendChild(noPropMsg);
      }
    } else {
      let hasAnyContent = false;
      for (let i = 0; i < nested.length; i++) {
        const el = nested[i];
        const props = nestedElementToProps(el);
        if (props.length === 0) continue;
        hasAnyContent = true;
        const sectionTitle = (el.Name ?? '').trim() || `Element ${i + 1}`;
        const section = this._buildSection(sectionTitle, props, false);
        this._presentationSections.push(section);
        this._scrollArea.appendChild(section);
      }
      if (!hasAnyContent) {
        const noPropMsg = document.createElement('div');
        noPropMsg.className = 'bg-panel-empty';
        noPropMsg.textContent = 'No matching properties found for this element.';
        this._scrollArea.appendChild(noPropMsg);
      }
    }
  };

  BgPropertiesPanel.prototype._applySearch = function (
    this: any,
    term: string
  ): void {
    const lc = term.toLowerCase().trim();

    (this._allRows as HTMLElement[]).forEach((row) => {
      if (!lc) {
        row.style.display = '';
      } else {
        const ds = (row as any).dataset as DOMStringMap;
        const matches =
          (ds['label'] ?? '').includes(lc) || (ds['value'] ?? '').includes(lc);
        row.style.display = matches ? '' : 'none';
      }
    });

    if (this._layersSection) this._refreshSectionVisibility(this._layersSection);
    if (this._bgSection) this._refreshSectionVisibility(this._bgSection);
    if (this._otherSection) this._refreshSectionVisibility(this._otherSection);
    (this._presentationSections as HTMLElement[]).forEach((section) => {
      this._refreshSectionVisibility(section);
    });
  };

  BgPropertiesPanel.prototype._refreshSectionVisibility = function (
    this: any,
    section: HTMLElement
  ): void {
    const rows = section.querySelectorAll<HTMLElement>('.bg-prop-row');
    const hasVisible = Array.from(rows).some((r) => r.style.display !== 'none');
    section.style.display = hasVisible ? '' : 'none';
  };

  return new (BgPropertiesPanel as any)(viewer.container);
}
